import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { withFormik } from "formik";
import * as Yup from "yup";
import get from "lodash/get";
import isArray from "lodash/isArray";
import objectToFormData from "object-to-formdata";
import PropTypes from "prop-types";
import Actions from "../actions";
import useAlert from "hooks/useAlert";
import set from "lodash/set";
import omit from "lodash/omit";

const MAIN_DEFAULT_PROPS = {
    primaryKey: "id",
    appendData: false,
    prependData: false,
    updateData: false,
    deleteData: false,
    normalizeData: "",
    sendAsFormData: false,
    expectBlob: false,
    blobFileName: "download.xlsx",
    isMulti: false,
    onSuccess: () => {},
    onError: () => {},
};

const Main = ({
    children,
    handleSubmit,
    submitForm,
    values,
    isSubmitting,
    setFieldValue,
    setFieldError,
    errors,
    setFieldTouched,
}) => (
    <form onSubmit={handleSubmit}>
        {children({ handleSubmit, submitForm, values, isSubmitting, setFieldValue, setFieldError, errors, setFieldTouched })}
    </form>
);

Main.propTypes = {
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    entity: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    url: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
    method: PropTypes.oneOf(['get', 'post', 'put', 'delete']).isRequired,
    primaryKey: PropTypes.string,
    fields: PropTypes.array.isRequired,
    appendData: PropTypes.bool,
    prependData: PropTypes.bool,
    updateData: PropTypes.bool,
    deleteData: PropTypes.bool,
    normalizeData: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    sendAsFormData: PropTypes.bool,
    expectBlob: PropTypes.bool,
    blobFileName: PropTypes.string,
    onSuccess: PropTypes.func,
    onError: PropTypes.func,
    isMulti: PropTypes.bool,
};

const EnhacedForm = withFormik({
    enableReinitialize: true,
    validationSchema: ({ fields }) => {

        if (!isArray(fields)) {
            return Yup.object().shape({});
        }

        let validationFields = {};

        fields.forEach(field => {
            let validationField;

            switch (field.type) {
                case "string":
                    validationField = Yup.string().typeError("Must be a string");
                    break;
                case "object":
                    validationField = Yup.object();
                    break;
                case "number":
                    validationField = Yup.number()
                        .transform((value, originalValue) => {
                            if (originalValue === "" || originalValue === null || originalValue === undefined) {
                                return undefined;
                            }
                            if (typeof originalValue === "string") {
                                const n = +String(originalValue).replaceAll(" ", "");
                                return Number.isFinite(n) ? n : NaN;
                            }
                            return value;
                        })
                        .typeError("Must be a number");
                    break;
                case "array":
                    validationField = Yup.array();
                    break;
                case "boolean":
                    validationField = Yup.boolean();
                    break;
                case "date":
                    validationField = Yup.date();
                    break;
                default:
                    // If a field has no explicit type, don't enforce runtime type checks.
                    // For fields with min/max, keep plain string validation for lengths.
                    validationField = field.min || field.max ? Yup.string() : Yup.mixed();
            }

            if (field.required) {
                validationField = validationField.required("Необходимо заполнить");
            }

            if (field.min) {
                validationField = validationField.min(field.min, "Too Short!");
            }

            if (field.max) {
                validationField = validationField.max(field.max, "Too Long!");
            }

            if (field.type === "number" && field.positive) {
                validationField = validationField.moreThan(0, "Необходимо заполнить");
            }

            validationField = validationField.nullable();

            validationFields[field.name] = validationField;
        });

        return Yup.object().shape(validationFields);
    },
    mapPropsToValues: ({ fields }) => {
        return isArray(fields) ? fields.reduce((prev, curr) => ({
            ...prev,
            [curr.name]: curr.isAbsolute ? curr.value : get(curr, 'value', "")
        }), {}) : {};
    },
    handleSubmit: (values, { props, setFieldError, setSubmitting, resetForm }) => {
        let {
            id,
            entity,
            name,
            url,
            params,
            method,
            primaryKey,
            isMulti,
            fields,
            appendData,
            prependData,
            updateData,
            deleteData,
            normalizeData,
            sendAsFormData,
            expectBlob,
            blobFileName,
            onSuccess = () => { },
            onError = () => { },
            FormAction,
            selfErrorMessage
        } = props;

        values = { ...values };

        if (typeof url === "function") {
            url = url({ ...values });
        }

        fields.forEach(field => {
            const hasSubmitValueFn =
                field.hasOwnProperty("onSubmitValue") &&
                typeof field.onSubmitValue === "function";
            const hasSubmitKey = field.hasOwnProperty("onSubmitKey");

            const fieldValue = get(values, field.name);
            const newValue = hasSubmitValueFn
                ? field.onSubmitValue(fieldValue, values)
                : fieldValue;
            let isDisabled = false;

            if (typeof field.disabled === "function") {
                isDisabled = field.disabled(values);
            } else {
                isDisabled = !!field.disabled;
            }

            if (isDisabled) {
                values = omit(values, field.name);
                return;
            }

            if (hasSubmitKey) {
                set(values, field.onSubmitKey, newValue);
                values = omit(values, field.name);
            } else if (hasSubmitValueFn) {
                set(values, field.name, newValue);
            }
        });

        if (sendAsFormData) {
            values = objectToFormData(values);
        }

        FormAction({
            id,
            entity,
            name,
            url,
            params,
            method,
            primaryKey,
            values,
            isMulti,
            appendData,
            prependData,
            updateData,
            deleteData,
            normalizeData,
            expectBlob,
            cb: {
                success: (data) => {
                    // Automatically detect and handle blob response
                    if (data instanceof Blob) {
                        // Create a download link for the blob
                        const url = window.URL.createObjectURL(data);
                        const link = document.createElement('a');
                        link.href = url;

                        // Extract filename from props or use default
                        let filename = blobFileName || 'download.xlsx';

                        link.setAttribute('download', filename);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                    }

                    // Always call onSuccess regardless of response type
                    onSuccess(data, resetForm);
                },
                error: (errors = []) => {
                    console.log(errors, "errors main")

                    if (!selfErrorMessage) {
                        new useAlert().showAlert("", { type: 'danger' });
                    }

                    if (Array.isArray(errors)) {
                        errors.map(({ field, message }) => setFieldError(field, message));
                    } else if (errors.errors) {
                        Object.keys(errors.errors).map(field => {
                            const error = errors.errors[field][0];
                            return setFieldError(field, error);
                        });
                    } else if (errors instanceof Object) {
                        Object.keys(errors).map(field => {
                            const error = errors[field][0];
                            return setFieldError(field, error);
                        });
                    }

                    onError(errors, setFieldError);
                },
                finally: (data) => {
                    console.log(data, "finally data")
                    setSubmitting(false);
                }
            }
        });
    }
})(Main);

const MainWithDefaults = (props) => (
    <EnhacedForm {...MAIN_DEFAULT_PROPS} {...props} />
);

const mapDispatchToProps = (dispatch) => bindActionCreators(
    {
        FormAction: Actions.Form.request
    },
    dispatch
);

export default connect(null, mapDispatchToProps)(MainWithDefaults);